import React from "react";
import { useApp } from "../../contexts/AppContext";
import { TEMARIO } from "../../constants/temario";

const ResultModal = ({ score, totalQuestions, onClose, onReview }) => {
  const { setView, setCurrentExam, currentExam } = useApp();
  const percentage = Math.round((score / totalQuestions) * 100);

  const shuffleArray = (array) => {
    const newArr = [...array];
    for (let i = newArr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
  };

  const getMessage = () => {
    if (percentage >= 90) return "¡Excelente! Dominas el temario.";
    if (percentage >= 70) return "¡Buen trabajo! Sigue practicando.";
    if (percentage >= 50)
      return "Vas por buen camino, repasa los temas con más dudas.";
    return "Sigue estudiando, ¡tú puedes mejorar!";
  };

  const getIcon = () => {
    if (percentage >= 90) return "🏆";
    if (percentage >= 70) return "🎯";
    if (percentage >= 50) return "📚";
    return "💪";
  };

  const handlePracticeAgain = () => {
    if (currentExam) {
      // 1. PRIMERO, LIMPIAR COMPLETAMENTE EL EXAMEN ACTUAL
      setCurrentExam(null);

      // 2. PEQUEÑO DELAY PARA ASEGURAR QUE EL ESTADO SE LIMPIE
      setTimeout(() => {
        // ENCONTRAR EL TEMA ORIGINAL EN EL TEMARIO
        const originalTopic = TEMARIO.find(
          (t) => t.id === currentExam.sourceId
        );

        if (originalTopic) {
          // CREAR PREGUNTAS NUEVAS - COMPLETAMENTE LIMPIAS, CON OPCIONES REMEZCLADAS
          const freshQuestions = originalTopic.questions.map((q) => {
            const correctText = q.o[q.c];
            const shuffledOptions = shuffleArray([...q.o]);
            return {
              q: q.q,
              o: shuffledOptions,
              c: shuffledOptions.indexOf(correctText),
              exp: q.exp,
              answered: false,
              selectedAnswer: null,
            };
          });

          // CREAR UN EXAMEN COMPLETAMENTE NUEVO
          const newExam = {
            title: originalTopic.title,
            questions: freshQuestions,
            sourceId: originalTopic.id,
            currentIdx: 0,
            score: 0,
            timer: 0,
          };

          // ACTUALIZAR EL ESTADO GLOBAL
          setCurrentExam(newExam);
          setView("exam");
          onClose();
        } else {
          // SI ES SIMULADOR, CREAR PREGUNTAS ALEATORIAS FRESCAS, CON OPCIONES REMEZCLADAS
          const allQuestions = TEMARIO.flatMap((t) => t.questions).map((q) => {
            const correctText = q.o[q.c];
            const shuffledOptions = shuffleArray([...q.o]);
            return {
              q: q.q,
              o: shuffledOptions,
              c: shuffledOptions.indexOf(correctText),
              exp: q.exp,
              answered: false,
              selectedAnswer: null,
            };
          });

          const shuffled = [...allQuestions]
            .sort(() => Math.random() - 0.5)
            .slice(0, 20);

          const newExam = {
            title: "Simulación Real",
            questions: shuffled,
            sourceId: "simulator",
            currentIdx: 0,
            score: 0,
            timer: 0,
          };

          setCurrentExam(newExam);
          setView("exam");
          onClose();
        }
      }, 50);
    }
  };

  const handleBackToDashboard = () => {
    setView("dashboard");
    setCurrentExam(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-xl z-[100] flex items-center justify-center p-6 overflow-y-auto animate-fade">
      <div className="bg-white rounded-[3.5rem] w-full max-w-xl p-12 text-center shadow-2xl">
        <div className="text-8xl mb-6">{getIcon()}</div>
        <h2 className="text-4xl font-black text-slate-900 mb-2">
          ¡Examen Completado!
        </h2>
        <p className="text-slate-500 mb-10 text-lg">{getMessage()}</p>

        <div className="grid grid-cols-2 gap-6 mb-12">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-[2rem] border border-blue-100">
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-2">
              Puntaje
            </p>
            <p className="text-4xl font-black text-blue-600">
              {score}/{totalQuestions}
            </p>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-[2rem] border border-emerald-100">
            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-2">
              Precisión
            </p>
            <p className="text-4xl font-black text-emerald-600">
              {percentage}%
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={onReview}
            className="w-full bg-emerald-600 text-white py-5 rounded-2xl font-black text-lg shadow-xl hover:bg-emerald-700 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            Revisar Respuestas
          </button>

          <button
            onClick={handlePracticeAgain}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-5 rounded-2xl font-black text-lg shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Practicar de Nuevo
          </button>

          <button
            onClick={handleBackToDashboard}
            className="w-full bg-white border-2 border-slate-200 text-slate-700 py-5 rounded-2xl font-bold text-lg hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Volver al Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultModal;
